import React from "react";
import { useState, useEffect } from "react";
import { Col, Collapse, Row, Table } from "reactstrap";
import PaginatedTable from "../../common/components/PaginatedTable";
import FilterInput from "../../common/components/FilterInput";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../common/lib/KaAlert";
import QueryView from "../../common/lib/QueryView";
import OverflowTooltip from "../../common/OverflowTooltip";
import { adminFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import { dateSort } from "../../common/utilities";
import useGetPastOrders, { DateRange, FilterField, PastOrder } from "../../requests/useGetPastOrders";
import FullscreenLoadingSpinner from "../../common/FullscreenLoadingSpinner";
import { KaSelect, KaSelectOption, isMultiValue } from "../../common/KaSelect";

const filterFieldOptions: KaSelectOption[] = [
  { value: 'OrderNumber', label: 'Order #' },
  { value: 'LoadoutGroup', label: 'Loadout Group' },
  { value: 'TransactionNumber', label: 'Transaction #' },
  { value: 'Product', label: 'Product' }
];

const dateRangeOptions: KaSelectOption[] = [
  { value: 'Days30', label: 'Last 30 Days' },
  { value: 'Days90', label: 'Last 90 Days' },
  { value: 'Days365', label: 'Last 365 Days' }
];

const filterFieldPlaceholders: Record<FilterField, string> = {
  OrderNumber: 'Filter by order number',
  LoadoutGroup: 'Filter by loadout group',
  TransactionNumber: 'Filter by transaction number',
  Product: 'Filter by product'
};

const OrderHistory = () => {
  const displayMessage = useDisplayMessage();

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [dateRange, setDateRange] = useState<DateRange>('Days30');
  const [filterField, setFilterField] = useState<FilterField>('OrderNumber');
  const [filterValue, setFilterValue] = useState('');
  const [debouncedFilterValue, setDebouncedFilterValue] = useState('');

  const [showPastOrderIds, setShowPastOrderIds] = useState<string[]>([]);

  const useGetPastOrdersQuery = useGetPastOrders(
    currentPage,
    dateRange,
    debouncedFilterValue ? filterField : undefined,
    debouncedFilterValue || undefined,
    {
      onSuccess: (resp) => {
        setTotalPages(resp.totalPages);
        setPageSize(resp.pageSize);
      },
      onError: err => displayMessage.fail(adminFacingErrorMessage(err))
    }
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterValue(filterValue);
    }, 600);

    return () => clearTimeout(handler);
  }, [filterValue]);

  const handleDateRangeChange = (range: DateRange) => {
    setCurrentPage(0);
    setDateRange(range);
  };

  const handleFilterValueChange = (value: string) => {
    setCurrentPage(0);
    setFilterValue(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getNetWeight = (pastOrder: PastOrder) => {
    if (pastOrder.grossWeight === null || pastOrder.tareWeight === null)
      return "";

    return (Number(pastOrder.grossWeight) - Number(pastOrder.tareWeight)).toString() + " lbs";
  }

  const toggleTransactionTimeline = (pastOrderId: string) => {
    const pastOrderIdIndex = showPastOrderIds.indexOf(pastOrderId);

    if (pastOrderIdIndex === -1) {
      setShowPastOrderIds([...showPastOrderIds, pastOrderId]);
    } else {
      const updatedIds = [...showPastOrderIds];
      updatedIds.splice(pastOrderIdIndex, 1)
      setShowPastOrderIds(updatedIds);
    }
  }

  type TimelineRow = {
    Step: string;
    Time: string | null;
    Location?: string | null;
    User?: string | null;
    Weight?: string | null;
  }

  const TransactionTimeline = ({ pastOrder, show, index }: { pastOrder: PastOrder, show: boolean, index: number }) => {

    if (!show) return <></>;

    var timelineRows: TimelineRow[] = [
      { Step: "Check-in", Time: pastOrder.checkedInAt },
      { Step: "Tare Weight", Time: pastOrder.tareAt, Location: pastOrder.tareScaleName, Weight: pastOrder.tareWeight },
      { Step: "Bay Directions", Time: pastOrder.arrivedAtBayAt, Location: pastOrder.bayName },
      { Step: "Dispensed", Time: pastOrder.dispensingCompletedAt },
      { Step: "Manually Loaded", Time: pastOrder.manuallyLoadedAt, User: pastOrder.loaderEmail },
      { Step: "Gross Weight", Time: pastOrder.grossAt, Location: pastOrder.grossScaleName, Weight: pastOrder.grossWeight },
      { Step: "Signed", Time: pastOrder.signedAt },
      { Step: "Completed", Time: pastOrder.completedAt },
    ]

    timelineRows = timelineRows.filter(r => r.Time || r.Location || r.User || r.Weight);

    const getStepColor = (step: string) => step.replace(" ", "-").toLowerCase()

    return <td colSpan={7} data-testid={`timeline-${index}`}>
      <ul className="tree">
        <Row className="mb-1 ps-2">
          <Col xs={2} />
          <Col xs={3} />
          <Col xs={2} className="ms-1">
            <span className="text-dark fw-semibold">Location</span>
          </Col>
          <Col xs={2} className="ms-1">
            <span className="text-dark fw-semibold">User</span>
          </Col>
          <Col xs={2} className="ms-1">
            <span className="text-dark fw-semibold">Weight</span>
          </Col>
        </Row>
        {timelineRows.map(timelineRow =>
          <li key={`timeline-${index}-${timelineRow.Step}`}>
            <Row>
              <Col xs={2} className="ms-1">
                <span className={"fw-semibold " + getStepColor(timelineRow.Step)}>
                  {timelineRow.Step}
                </span>
              </Col>
              <Col xs={3} className="ms-1">
                {timelineRow.Time === null || timelineRow.Time === '' ? '' : new Date(timelineRow.Time as string).toLocaleString() }
              </Col>
              <Col xs={2} className="ms-1">
                {timelineRow.Location ?? ""}
              </Col>
              <Col xs={2} className="ms-1">
                {timelineRow.User ?? ""}
              </Col>
              <Col xs={2} className="ms-1">
                {timelineRow.Weight ? timelineRow.Weight + " lbs" : ""}
              </Col>
            </Row>
          </li>
        )}
      </ul>
    </td>
  }

  return <div>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <h2>Order History</h2>
    <Row className="align-items-end mb-3">
      <Col xs={3}>
        <FilterInput
          placeholder={filterFieldPlaceholders[filterField]}
          value={filterValue}
          onFilterTextChange={handleFilterValueChange}
        />
      </Col>
      <Col xs={2}>
        <label className="form-label fw-bold">Filter By</label>
        <KaSelect
          options={filterFieldOptions}
          value={filterFieldOptions.find(o => o.value === filterField)}
          onChange={(valueObject) => {
            if (!isMultiValue(valueObject) && valueObject) {
              setCurrentPage(0);
              setFilterField(valueObject.value as FilterField);
              setFilterValue('');
            }
          }}
        />
      </Col>
      <Col xs={2}>
        <label className="form-label fw-bold">Date Range</label>
        <KaSelect
          options={dateRangeOptions}
          value={dateRangeOptions.find(o => o.value === dateRange)}
          onChange={(valueObject) => {
            if (!isMultiValue(valueObject) && valueObject) {
              handleDateRangeChange(valueObject.value as DateRange);
            }
          }}
        />
      </Col>
    </Row>
    <QueryView
      query={useGetPastOrdersQuery}
      renderLoading={() => <FullscreenLoadingSpinner />}
      renderData={paginatedPastOrders => {
        return <>
          <PaginatedTable
            onPageChange={handlePageChange}
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}>
            <Table borderless className="mt-1">
              <thead>
                <tr className="border-bottom border-dark border-1">
                  <th className="col-2" style={{ verticalAlign: 'top' }}>Transaction(s)</th>
                  <th className="col-2" style={{ verticalAlign: 'top' }}>Order(s)</th>
                  <th className="col-2" style={{ verticalAlign: 'top' }}>Customer(s)</th>
                  <th className="col-2" style={{ verticalAlign: 'top' }}>Driver</th>
                  <th className="col-3" style={{ verticalAlign: 'top' }}>Product(s)</th>
                  <th className="col-1" style={{ verticalAlign: 'top' }}>Order Quantity</th>
                  <th className="col-1" style={{ verticalAlign: 'top' }}>Net Weight</th>
                  <th className="col-1" style={{ verticalAlign: 'top' }} />
                </tr>
              </thead>
              <tbody>
                {dateSort(paginatedPastOrders.items, 'completedAt')
                  .map((pastOrder, i) =>
                    <React.Fragment key={pastOrder.stagedOrderId}>
                      <tr className="border-top">
                        <td className="py-2 align-middle">
                          <OverflowTooltip
                            className="mb-0"
                            text={pastOrder.transactionNumbers ?? ""}
                          />
                        </td>
                        <td className="py-2 align-middle">
                          <OverflowTooltip
                            className="mb-0"
                            text={pastOrder.orderNumber ?? pastOrder.orderNumbers?.join(", ") ?? ""}
                          />
                        </td>
                        <td className="py-2 align-middle">
                          <OverflowTooltip
                            className="mb-0"
                            text={pastOrder.customerNames?.join(", ") ?? ""}
                          />
                        </td>
                        <td className="py-2 align-middle">
                          <OverflowTooltip
                            className="mb-0"
                            text={pastOrder.driverEmail ?? ""}
                          />
                        </td>
                        <td className="py-2 align-middle">
                          <OverflowTooltip
                            className="mb-0"
                            text={pastOrder.products?.join(", ") ?? ""}
                          />
                        </td>
                        <td className="py-2 align-middle">
                          <p className="mb-0">
                            {pastOrder.quantity ? pastOrder.quantity + " lbs" : ""}
                          </p>
                        </td>
                        <td className="py-2 align-middle">
                          <p className="mb-0">
                            {getNetWeight(pastOrder)}
                          </p>
                        </td>
                        <td className="py-2 align-middle">
                          <button
                            type="button"
                            className="btn btn-icon btn-ghost-primary btn-link"
                            data-testid={`showTimeline-${i}`}
                            onClick={() => toggleTransactionTimeline(pastOrder.id)}>
                            {<i className={showPastOrderIds.includes(pastOrder.id) ? 'bi-caret-up-square' : 'bi-caret-down-square'} />}
                          </button>
                        </td>
                      </tr>
                      <Collapse
                        isOpen={showPastOrderIds.includes(pastOrder.id)}
                        tag={'tr'}
                        className='border-start border-end'>
                        <TransactionTimeline
                          index={i}
                          pastOrder={pastOrder}
                          show={showPastOrderIds.includes(pastOrder.id)} />
                      </Collapse>
                    </React.Fragment>
                  )}
              </tbody>
            </Table>
          </PaginatedTable>
        </>
      }} />
    </div>
}

export default OrderHistory;
